import { Controller } from "@hotwired/stimulus"
import { sendJSON } from "../api.js"

// Tabela leadów — część KLIENCKA: zaznaczanie wierszy + masowe dodanie do koszyka.
//
// Sortowanie i paginacja są SERVER-SIDE (parametry URL; wiersze renderuje kontroler
// `catalog` z DRF) — ten kontroler ich nie dotyka.
//
// "Dodaj zaznaczone" POST-uje {listing_ids} na /api/cart/add-bulk/; API zwraca
// {added: [...]} — odbijamy stan TYLKO dla realnie dodanych (część mogła zostać
// w międzyczasie zarezerwowana przez kogoś innego).
export default class extends Controller {
  static targets = ["row", "bulkbar", "selCount", "addBtn"]
  static values  = {
    addUrl: String,
    purchasingEnabled: { type: Boolean, default: true }, // false = personel jeszcze nie zweryfikował klienta — blokuje "Dodaj zaznaczone"
  }

  connect() {
    // Zaznaczanie wierszy zostaje możliwe (nieszkodliwe) — blokujemy tylko samo dodanie:
    // przycisk disabled (backend i tak 403-uje, ale nie może wyglądać na klikalny) +
    // addSelected() poniżej ma tę samą blokadę jako no-op na wszelki wypadek.
    if (this.hasAddBtnTarget && !this.purchasingEnabledValue) {
      this.addBtnTarget.disabled = true
      this.addBtnTarget.title = "Konto nie zostało jeszcze zweryfikowane — skontaktuj się z nami, aby aktywować zakupy."
    }
    this.#refresh()
  }

  toggleRow() { this.#refresh() }

  clear() {
    this.rowTargets.forEach(cb => { cb.checked = false; this.#markRow(cb, false) })
    this.#refresh()
  }

  async addSelected() {
    if (!this.purchasingEnabledValue) return // konto niezweryfikowane — patrz connect()
    // Pomiń pozycje już będące w koszyku — re-POST tylko odświeżałby im TTL rezerwacji.
    const ids = this.#selectedIds().filter(id => {
      const btn = document.querySelector(`.ls-add-btn[data-cart-id-param="${id}"]`)
      return !btn?.classList.contains("is-in-cart")
    })
    if (!ids.length) {
      this.clear()
      return
    }

    // Bulk selection may mix modes — one row picked as slot, another as exclusivity.
    // The backend accepts one mode per request, so we group by mode and make one request
    // per group. Rows without a mode picker (BUR leads) default to "exclusivity".
    // WHY group rather than per-item payload: avoids a backend serializer change (out of lane)
    // while still honouring every user's per-row choice without silent override.
    const groups = {}
    ids.forEach(id => {
      const btn = document.querySelector(`.ls-add-btn[data-cart-id-param="${id}"]`)
      const mode = btn?.dataset.cartModeParam ?? "exclusivity"
      if (!groups[mode]) groups[mode] = []
      groups[mode].push(id)
    })

    const added = []
    for (const [mode, modeIds] of Object.entries(groups)) {
      const res = await sendJSON("POST", this.addUrlValue, { listing_ids: modeIds, mode })
      if (res.ok) {
        const batch = res.data?.added ?? []
        added.push(...batch)
      }
    }

    // Reflect per-row outcomes.
    // Rows in `added`: accepted → mark in-cart.
    // Rows sent but not in `added`: refused (mode unavailable) → disable and inform the
    // user so they are not silently downgraded to a product they did not select.
    ids.forEach(id => {
      const btn = document.querySelector(`.ls-add-btn[data-cart-id-param="${id}"]`)
      if (!btn) return
      if (added.includes(id)) {
        if (!btn.classList.contains("is-in-cart")) {
          btn.classList.add("is-in-cart")
          const mono = btn.querySelector(".ls-mono")
          const label = btn.querySelector("[data-cart-label]")
          if (mono)  mono.textContent = "✓"
          if (label) label.textContent = "W koszyku"
          const row = btn.closest(".ls-lead-row")
          if (row) row.classList.add("is-selected")
        }
      } else {
        // Mode unavailable — do not silently downgrade; tell the user which row failed.
        btn.disabled = true
        const mono = btn.querySelector(".ls-mono")
        const label = btn.querySelector("[data-cart-label]")
        if (mono)  mono.textContent = "×"
        if (label) label.textContent = "Niedostępny"
      }
    })

    // Odśwież drawer koszyka (sumy + pozycje) przez kontroler `cart` na tej stronie.
    const cartEl = document.querySelector('[data-controller~="cart"]')
    if (cartEl) {
      const ctrl = this.application.getControllerForElementAndIdentifier(cartEl, "cart")
      if (ctrl) await ctrl.load()
    }
    this.clear()
  }

  // --- prywatne ---
  #selectedIds() { return this.rowTargets.filter(cb => cb.checked).map(cb => Number(cb.value)) }

  #refresh() {
    const n = this.#selectedIds().length
    this.rowTargets.forEach(cb => this.#markRow(cb, cb.checked))
    if (this.hasBulkbarTarget)  this.bulkbarTarget.hidden = n === 0
    if (this.hasSelCountTarget) this.selCountTarget.textContent = n
  }

  #markRow(cb, on) {
    const row = cb.closest(".ls-lead-row")
    if (!row) return
    // Podświetlenie zostaje, gdy wiersz jest w koszyku (is-selected pełni też rolę "in cart").
    const inCart = row.querySelector(".ls-add-btn")?.classList.contains("is-in-cart") ?? false
    row.classList.toggle("is-selected", on || inCart)
  }
}
